import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAuthToken, getAuthToken } from "@/lib/api";

const AuthCtx = createContext(null);

/** Retry the given axios call once after 800ms if it fails with a
 *  network-layer error (offline / DNS / ad-blocker / timeout). Business errors
 *  (401 / 400 / 422) are thrown immediately so the UI can show them. */
async function withRetry(fn) {
    try {
        return await fn();
    } catch (err) {
        const isNetwork = !err?.response && (
            err?.code === "ERR_NETWORK" ||
            err?.code === "ECONNABORTED" ||
            err?.message === "Network Error" ||
            /timeout/i.test(err?.message || "")
        );
        if (!isNetwork) throw err;
        await new Promise((r) => setTimeout(r, 800));
        return await fn();
    }
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (!getAuthToken()) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setAuthToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const login = async (email, password) => {
        const { data } = await withRetry(() => api.post("/auth/login", { email, password }));
        setAuthToken(data.token);
        setUser(data.user);
        return data.user;
    };
    const register = async (payload) => {
        // If URL has ?ref=CODE, pipe it into the register payload
        try {
            const url = new URL(window.location.href);
            const ref = url.searchParams.get("ref");
            if (ref && !payload.referral_code) payload = { ...payload, referral_code: ref };
        } catch { /* ignore malformed URL */ }
        const { data } = await withRetry(() => api.post("/auth/register", payload));
        setAuthToken(data.token);
        setUser(data.user);
        return data.user;
    };
    const logout = () => { setAuthToken(null); setUser(null); };

    const faucet = async () => {
        const { data } = await api.post("/auth/faucet");
        setUser(data);
        return data;
    };

    return (
        <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, faucet }}>
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    const v = useContext(AuthCtx);
    if (!v) throw new Error("useAuth outside AuthProvider");
    return v;
}
